import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Pause,
  MessageSquare,
  BookOpen,
  LogOut,
  BarChart,
  Loader2,
  MessageSquarePlus,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { nanoid } from 'nanoid';
import { useNavigate } from 'react-router-dom';
import AssistantOperatorMessage from '../components/messages/AssistantOperatorMessage';
import OperatorMessage from '../components/messages/OperatorMessage';
import UserMessage from '../components/UserMessage';
import CoachFeedbackPanel from '../components/CoachFeedbackPanel';
import { hideInfoCardsForMessage } from '../services/backendService';
import type { ConversationItem } from '../types/conversationItem.types';
import type { PersonaGroup } from '../types/personaGroups.types';
import { useTranslate, useLanguage } from '../lib/i18n';
import {
  useCreateMessage,
  useConversation,
  useCoachFeedback,
  useMessageInput,
  useConfig,
} from '../hooks';
import { useGenerateOperatorReport } from '../hooks/mutations/useGenerateOperatorReport';
import { logoutOperator } from '../utils/authUtils';
import { exportReportAsPdf } from '../utils/pdfExport';
import { suppressDuplicateInfoCards } from '../utils/infoCardSuppression';
import MarkdownRenderer from '../components/MarkdownRenderer';
import VendorLogo from '../components/VendorLogo';

const MAX_ROWS = 3;
const LINE_HEIGHT = 24;
const MIN_HEIGHT = LINE_HEIGHT;
const MAX_HEIGHT = MAX_ROWS * LINE_HEIGHT;

interface Props {
  topic: string | null;
  setTopic: (topic: string) => void;
}

const OperatorChatInterface = ({ topic, setTopic }: Props) => {
  const {
    conversationId,
    conversationCreatedAt,
    conversation,
    isCreatingConversation,
    initializeConversation,
    startConversation,
    updateConversation,
    removeMessages,
    clearAllOptions,
    resetConversation,
    chatContainerRef,
    messagesEndRef,
  } = useConversation();

  const { coachMessages, addCoachMessage, showCoachPanel, toggleCoachPanel, clearCoachMessages } =
    useCoachFeedback();

  const { language } = useLanguage();
  const { data: chatbotConfig } = useConfig();
  const [selectedPersonaGroups, setSelectedPersonaGroups] = useState<PersonaGroup[]>([]);
  const [isPersonaGroupsExpanded, setIsPersonaGroupsExpanded] = useState(true);
  const [isStartingMessageExpanded, setIsStartingMessageExpanded] = useState(true);

  // Get available persona groups from config (defaults to youngAdults if empty)
  const availablePersonaGroups =
    chatbotConfig?.availablePersonaGroups && chatbotConfig.availablePersonaGroups.length > 0
      ? chatbotConfig.availablePersonaGroups
      : ['youngAdults'];

  const [showReportOverlay, setShowReportOverlay] = useState(false);
  const {
    mutate: generateReport,
    data: reportData,
    isPending: isLoadingReport,
    error: reportErrorRaw,
    reset: resetReport,
  } = useGenerateOperatorReport();
  const operatorReport = reportData?.report || null;
  const reportError = reportErrorRaw
    ? reportErrorRaw instanceof Error
      ? reportErrorRaw.message
      : String(reportErrorRaw)
    : null;

  const [isMobileView, setIsMobileView] = useState(false);
  const [isTabletView, setIsTabletView] = useState(false);
  const [hideInfoCards, setHideInfoCards] = useState<boolean>(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const t = useTranslate();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const seenInfoCardStackIds = useRef<Set<string>>(new Set());
  const navigate = useNavigate();

  const { mutateAsync: createMessage } = useCreateMessage();

  const handleSendMessage = useCallback(
    async (messageContent: string, abortSignal?: AbortSignal) => {
      clearAllOptions();

      const userMessage: ConversationItem = {
        id: nanoid(),
        author: 'OPERATOR',
        content: messageContent,
        time: new Date().toUTCString(),
        options: [],
      };

      updateConversation(userMessage);

      const loadingMessage: ConversationItem = {
        id: nanoid(),
        author: 'ASSISTANT',
        content: t('common.labels.writing'),
        time: new Date().toUTCString(),
        options: [],
      };

      updateConversation(loadingMessage);

      try {
        let activeConversationId = conversationId;

        if (!activeConversationId) {
          activeConversationId = await startConversation(selectedPersonaGroups);
        }

        const response = await createMessage({
          conversationId: activeConversationId,
          role: 'OPERATOR',
          content: messageContent,
          topic,
          language,
          abortSignal,
        });

        // Check if the request was aborted during the async operation
        if (abortSignal?.aborted) {
          removeMessages([loadingMessage.id]);
          return;
        }

        removeMessages([loadingMessage.id]);

        const normalizedResponse = suppressDuplicateInfoCards(
          response as ConversationItem,
          seenInfoCardStackIds.current
        );
        updateConversation(normalizedResponse);

        if (normalizedResponse.coachFeedback) {
          const newCoachMessage: ConversationItem = {
            id: nanoid(),
            author: 'COACH',
            content: normalizedResponse.coachFeedback,
            time: new Date().toUTCString(),
          };

          setTimeout(() => {
            addCoachMessage(newCoachMessage);
          }, 500);
        }

        if (normalizedResponse.detectedTopic) {
          setTopic(normalizedResponse.detectedTopic);
        }
      } catch {
        // Don't handle errors if we aborted the request
        if (abortSignal?.aborted) {
          removeMessages([loadingMessage.id]);
          return;
        }

        removeMessages([loadingMessage.id]);

        const errorMessage: ConversationItem = {
          id: nanoid(),
          author: 'ASSISTANT',
          content: t('common.errors.generic'),
          time: new Date().toUTCString(),
          options: [],
          isError: true,
        };

        updateConversation(errorMessage);
      }
    },
    [
      conversationId,
      selectedPersonaGroups,
      topic,
      language,
      t,
      clearAllOptions,
      updateConversation,
      removeMessages,
      createMessage,
      startConversation,
      addCoachMessage,
      setTopic,
    ]
  );

  const {
    message,
    validationError,
    isSendingMessage,
    handleMessageChange,
    handleSend,
    handleCancel,
    handleKeyDown,
  } = useMessageInput({
    onSend: handleSendMessage,
    isOperatorChat: true,
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
      setIsTabletView(window.innerWidth >= 768 && window.innerWidth < 1024);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = `${Math.min(Math.max(scrollHeight, MIN_HEIGHT), MAX_HEIGHT)}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [message]);

  useEffect(() => {
    initializeConversation();
  }, [initializeConversation]);

  useEffect(() => {
    seenInfoCardStackIds.current = new Set();
  }, [conversationId]);

  const handleHideInfoCards = (messageId: string) => {
    setHideInfoCards(true);

    if (messageId) {
      try {
        hideInfoCardsForMessage({ messageId }).catch(() => {
          console.error('Failed to update info cards status');
        });
      } catch (error) {
        console.error('Failed to hide info cards:', error);
      }
    }
  };

  const handleLogout = () => {
    logoutOperator();
    navigate('/training/login');
  };

  const handleNewChat = () => {
    resetConversation();
    clearCoachMessages();
    setSelectedPersonaGroups(availablePersonaGroups);
    setIsPersonaGroupsExpanded(true);
    setIsStartingMessageExpanded(true);
    setShowReportOverlay(false);
    resetReport();
    setExportError(null);
  };

  const handleRequestReport = () => {
    if (conversationId) {
      generateReport({ conversationId, language });
      setShowReportOverlay(true);
    } else {
      console.error('No conversation ID found');
    }
  };

  const handleCloseReport = () => {
    setShowReportOverlay(false);
    resetReport();
  };

  const handleExportReport = () => {
    if (!operatorReport || !conversationCreatedAt) return;

    try {
      // Clear any previous export errors
      setExportError(null);

      // Export the report as PDF using the utility function
      exportReportAsPdf({
        content: operatorReport,
        title: t('operatorChatbot.report.title', { defaultValue: 'Conversation Summary' }),
        timestamp: conversationCreatedAt,
        language: language as 'fi' | 'en' | 'sv',
        conversationId: conversationId || undefined,
      });
    } catch (error) {
      console.error('Failed to export report:', error);
      setExportError(t('operatorChatbot.report.exportError'));

      // Clear error after 5 seconds
      setTimeout(() => setExportError(null), 5000);
    }
  };

  const togglePersonaGroup = (group: PersonaGroup) => {
    if (hasUserMessage) return;

    setSelectedPersonaGroups((prev) =>
      prev.includes(group) ? prev.filter((g) => g !== group) : [...prev, group]
    );
  };

  const shouldShowCoachPanel = showCoachPanel;
  const hasUserMessage = conversation.some((messageItem) => messageItem.author === 'OPERATOR');

  useEffect(() => {
    if (hasUserMessage) {
      setIsStartingMessageExpanded(false);
      setIsPersonaGroupsExpanded(false);
    }
  }, [hasUserMessage]);

  useEffect(() => {
    if (
      !hasUserMessage &&
      selectedPersonaGroups.length === 0 &&
      availablePersonaGroups.length > 0
    ) {
      setSelectedPersonaGroups(availablePersonaGroups);
    }
  }, [availablePersonaGroups, hasUserMessage, selectedPersonaGroups.length]);

  return (
    <div className="fixed inset-0 flex flex-col bg-background text-popover-foreground">
      {showReportOverlay && operatorReport && (
        <div className="fixed inset-0 z-50 bg-black/50">
          <div className="h-full flex">
            {/* This div matches the chat area width */}
            <div className="flex-1 flex items-center justify-center">
              <div className="w-full max-w-3xl max-h-[90vh] bg-popover rounded-lg shadow-lg overflow-y-auto flex flex-col mx-4">
                <div className="p-4 flex justify-between items-center border-b">
                  <h2 className="text-xl font-semibold">{t('operatorChatbot.report.title')}</h2>
                  <button
                    onClick={handleCloseReport}
                    className="hover:text-primary"
                    aria-label="Close report"
                  >
                    ✕
                  </button>
                </div>
                <div className="p-6 overflow-y-auto grow prose prose-invert max-w-none prose-headings:my-3 prose-p:my-2 prose-ul:my-2 prose-li:my-1">
                  <MarkdownRenderer markdown={operatorReport} />
                </div>
                <div className="p-4 border-t">
                  {exportError && (
                    <div className="mb-2 p-2 border border-destructive bg-destructive/15 text-destructive rounded-md text-sm text-center">
                      {exportError}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={handleExportReport}
                      className="flex-1 py-2 bg-secondary text-secondary-foreground rounded-[10px] hover:bg-secondary/90"
                    >
                      {t('operatorChatbot.report.exportButton')}
                    </button>
                    <button
                      onClick={() => {
                        setShowReportOverlay(false);
                        resetReport();
                        setExportError(null);
                      }}
                      className="flex-1 py-2 bg-primary text-primary-foreground rounded-[10px] hover:bg-primary/90"
                    >
                      {t('common.buttons.close')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {/* Spacer for coach panel area */}
            {shouldShowCoachPanel && !isMobileView && (
              <div className={isTabletView ? 'w-64' : 'w-80'} />
            )}
          </div>
        </div>
      )}

      {/* Header - Fixed at top */}
      <div className="border-b bg-sidebar text-sidebar-foreground h-14">
        <div className="flex h-full">
          {/* Main header content - matches chat area width */}
          <div className="flex-1 flex items-center px-4">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center">
                <VendorLogo className="h-8 w-12 bg-left! mr-3" usage="header" />
                <h1 className="font-medium text-sm md:text-base">
                  {isMobileView ? t('operatorChatbot.titleShort') : t('operatorChatbot.title')}
                </h1>
              </div>
              {!isMobileView ? (
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleNewChat}
                    className="flex items-center hover:text-primary p-1 md:p-0 transition-colors"
                    aria-label={t('operatorChatbot.newChatLabel')}
                  >
                    <MessageSquarePlus className="h-4 w-4 md:h-5 md:w-5" />
                    <span className="text-sm ml-2">{t('operatorChatbot.newChat')}</span>
                  </button>

                  <div className="relative">
                    <button
                      onClick={handleRequestReport}
                      disabled={isLoadingReport || !conversationId || !hasUserMessage}
                      className={`flex items-center p-1 md:p-0 transition-colors ${
                        isLoadingReport || !conversationId || !hasUserMessage
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:text-primary'
                      }`}
                      aria-label={t('operatorChatbot.report.requestButtonLabel')}
                    >
                      {isLoadingReport ? (
                        <Loader2 className="h-4 w-4 md:h-5 md:w-5 animate-spin" />
                      ) : (
                        <BarChart className="h-4 w-4 md:h-5 md:w-5" />
                      )}
                      <span className="text-sm ml-2">
                        {t('operatorChatbot.report.requestButtonLabelShort')}
                      </span>
                    </button>
                    {reportError && (
                      <p className="absolute -bottom-5 right-0 text-xs text-destructive whitespace-nowrap">
                        {reportError}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                // Mobile view - show both buttons with icons only
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleNewChat}
                    className="flex items-center hover:text-primary p-1 transition-colors"
                    aria-label={t('operatorChatbot.newChatLabel')}
                  >
                    <MessageSquarePlus className="h-4 w-4" />
                  </button>

                  <button
                    onClick={handleRequestReport}
                    disabled={isLoadingReport || !conversationId || !hasUserMessage}
                    className={`flex items-center hover:text-primary p-1 transition-colors ${
                      isLoadingReport || !conversationId || !hasUserMessage
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                    }`}
                    aria-label={t('operatorChatbot.report.requestButtonLabel')}
                  >
                    {isLoadingReport ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <BarChart className="h-4 w-4" />
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Logout button area - aligned with coach panel */}
          {shouldShowCoachPanel && !isMobileView && (
            <div
              className={`flex items-center justify-start px-4 border-l ${
                isTabletView ? 'w-64' : 'w-80'
              }`}
            >
              <button
                onClick={handleLogout}
                className="flex items-center hover:text-primary"
                aria-label="Logout"
              >
                <LogOut className="h-4 w-4 md:h-5 md:w-5" />
                <span className="text-sm ml-2">Logout</span>
              </button>
            </div>
          )}

          {/* Mobile view - add logout button */}
          {isMobileView && (
            <div className="flex items-center px-2">
              <button
                onClick={handleLogout}
                className="flex items-center hover:text-primary p-1"
                aria-label="Logout"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main content container with flex layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Chat area container - contains both messages and input */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Messages scrollable area */}
          <div ref={chatContainerRef} className="flex-1 overflow-y-auto">
            <div className="mx-auto max-w-2xl px-4 pb-4">
              {conversation.map((messageItem: ConversationItem, index: number) => {
                const prevMessage = index > 0 ? conversation[index - 1] : null;

                if (messageItem.author === 'COACH') {
                  return null;
                }

                // Display system messages as informational messages (collapsible)
                if (messageItem.author === 'SYSTEM') {
                  return (
                    <div key={messageItem.id}>
                      <div className="mt-2 mb-4 bg-card rounded-lg overflow-hidden">
                        <button
                          onClick={() => setIsStartingMessageExpanded((prev) => !prev)}
                          className="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-card-foreground hover:bg-muted/30 transition-colors text-left"
                        >
                          <span>
                            {t('operatorChatbot.startingMessage.title', {
                              defaultValue: 'Ohjeet',
                            })}
                          </span>
                          {isStartingMessageExpanded ? (
                            <ChevronUp className="h-4 w-4 shrink-0" />
                          ) : (
                            <ChevronDown className="h-4 w-4 shrink-0" />
                          )}
                        </button>
                        {isStartingMessageExpanded && (
                          <div className="px-4 pb-4 text-sm text-card-foreground whitespace-pre-wrap">
                            {messageItem.content}
                          </div>
                        )}
                      </div>

                      {/* Persona Group Selection - below starting message */}
                      {availablePersonaGroups.length > 0 && (
                        <div className="mb-4 bg-muted/30 rounded-lg overflow-hidden">
                          <button
                            onClick={() => setIsPersonaGroupsExpanded((prev) => !prev)}
                            className="w-full px-4 py-2 flex items-center justify-between text-sm font-medium text-muted-foreground hover:bg-muted/50 transition-colors"
                          >
                            <span>
                              {t('operatorChatbot.personaGroups.title', {
                                defaultValue: 'Persona groups:',
                              })}
                            </span>
                            {isPersonaGroupsExpanded ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </button>
                          {isPersonaGroupsExpanded && (
                            <div className="px-4 pb-3 flex flex-wrap gap-2">
                              {availablePersonaGroups.map((group) => (
                                <label
                                  key={group}
                                  className={`flex items-center gap-1.5 text-sm rounded px-2 py-1 ${
                                    hasUserMessage
                                      ? 'cursor-not-allowed opacity-60'
                                      : 'cursor-pointer hover:bg-muted'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedPersonaGroups.includes(group)}
                                    onChange={() => togglePersonaGroup(group)}
                                    disabled={hasUserMessage}
                                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-50"
                                  />
                                  <span>
                                    {t(`operatorChatbot.personaGroups.${group}`, {
                                      defaultValue: group,
                                    })}
                                  </span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                }

                if (messageItem.author === 'OPERATOR') {
                  return (
                    <div key={messageItem.id} className="mb-4">
                      <OperatorMessage
                        content={messageItem.content}
                        time={messageItem.time}
                        isSameAuthorAsPrev={prevMessage?.author === 'OPERATOR'}
                      />
                    </div>
                  );
                }

                const MessageComponent =
                  messageItem.author === 'ASSISTANT' ? AssistantOperatorMessage : UserMessage;

                const displayHeader = !(
                  messageItem.author === 'USER' &&
                  prevMessage &&
                  prevMessage.author === 'USER'
                );

                if (!(messageItem.content || messageItem.special)) return null;

                return (
                  <div key={messageItem.id || index} className="mb-4">
                    <MessageComponent
                      {...messageItem}
                      content={messageItem.content}
                      time={messageItem.time}
                      showInfoCards={!!messageItem.showInfoCards}
                      infoCardsContext={messageItem.infoCardsContext}
                      topic={topic}
                      hideInfoCards={hideInfoCards}
                      onHideInfoCards={() => handleHideInfoCards(messageItem.id)}
                      displayHeader={displayHeader}
                    />
                  </div>
                );
              })}

              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Chat input - Now inside chat area container */}
          <div className="bg-background p-4 shadow-2xl">
            <div className="relative mx-auto max-w-2xl">
              <div className="flex items-center rounded-[10px] border bg-input/25 p-2">
                <div className="relative grow">
                  <textarea
                    ref={textareaRef}
                    value={message}
                    onChange={handleMessageChange}
                    onKeyDown={handleKeyDown}
                    className={`prevent-zoom w-full resize-none overflow-y-auto px-3 py-2 text-sm focus:outline-none ${validationError ? 'border-destructive' : 'border-secondary'} ${validationError ? 'focus:border-destructive' : 'focus:border-secondary'}`}
                    disabled={isSendingMessage || isCreatingConversation}
                    rows={1}
                  />
                  {!message && (
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                      {isSendingMessage
                        ? t('aiChatbot.waitingForResponse')
                        : t('operatorChatbot.writeMessagePrompt')}
                    </span>
                  )}
                </div>
                {isSendingMessage ? (
                  <button
                    onClick={handleCancel}
                    className="ml-2 flex items-center justify-center rounded-[10px] bg-muted px-4 py-2 transition-colors hover:bg-muted/50"
                    aria-label={t('aiChatbot.cancel')}
                  >
                    <Pause className="h-5 w-5" />
                  </button>
                ) : (
                  <button
                    onClick={handleSend}
                    className={`ml-2 rounded-[10px] bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 ${!message.trim() || isCreatingConversation ? 'cursor-not-allowed opacity-50' : ''}`}
                    disabled={!message.trim() || isCreatingConversation}
                  >
                    {t('common.buttons.send')}
                  </button>
                )}
              </div>
              {validationError && (
                <p className="absolute -bottom-5 my-1 text-xs text-destructive">
                  {validationError}
                </p>
              )}

              {/* Coach feedback button - below input but still inside chat area */}
              {isMobileView && !showCoachPanel && (
                <button
                  onClick={toggleCoachPanel}
                  className="mt-4 w-full rounded-[10px] bg-primary py-2 text-sm font-medium text-primary-foreground flex items-center justify-center"
                  aria-label={t('operatorChatbot.viewCoachFeedback')}
                >
                  <BookOpen className="h-4 w-4 mr-2" />
                  {t('operatorChatbot.viewCoachFeedback')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Coach panel - only show on desktop or when manually opened on mobile */}
        {shouldShowCoachPanel && (
          <div
            className={`border-l flex flex-col ${
              isMobileView
                ? 'absolute inset-0 z-10 bg-background'
                : isTabletView
                  ? 'w-64 shrink-0'
                  : 'w-80 shrink-0'
            }`}
          >
            {isMobileView ? (
              <div className="flex items-center justify-between border-b px-4 py-3 h-14">
                <h2 className="text-lg font-medium">{t('operatorChatbot.coachFeedback')}</h2>
                <button
                  onClick={toggleCoachPanel}
                  className="text-foreground hover:text-primary text-xl"
                  aria-label="Close coach feedback"
                >
                  ✕
                </button>
              </div>
            ) : (
              <div className="px-4 py-3 flex flex-col">
                <h2 className="text-md font-medium">{t('operatorChatbot.feedbackPanel.title')}</h2>
              </div>
            )}
            <div className="flex-1 overflow-hidden flex flex-col">
              <CoachFeedbackPanel messages={coachMessages} />
            </div>

            {isMobileView && (
              <div className="px-4 py-4 bg-background border-t">
                <button
                  onClick={toggleCoachPanel}
                  className="w-full rounded-[10px] bg-primary py-3 text-sm font-medium text-primary-foreground flex items-center justify-center"
                >
                  <MessageSquare className="h-5 w-5 mr-2" />
                  {t('operatorChatbot.backToChat')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OperatorChatInterface;
